import { ExternalLink, UserRound } from "lucide-react";
import { useContent } from "../../context/SiteContext";
import { mediaUrl } from "../../lib/api";
import { Container, SectionHeader, MoreLink } from "./Common";
import { Person } from "../../pages/OfficeBearers";

const PlayerCard = ({ p }) => (
  <article className="card overflow-hidden" data-testid={`featured-player-${p.id}`}>
    <div className="aspect-[4/5] bg-slate-100 overflow-hidden">
      {p.photo ? <img src={mediaUrl(p.photo)} alt={p.name} className="h-full w-full object-cover object-top" /> : <div className="h-full grid place-items-center text-slate-300"><UserRound className="h-16 w-16" /></div>}
    </div>
    <div className="p-5">
      {p.age_category && <span className="rounded-full bg-blue-50 text-blue-700 px-2.5 py-0.5 text-xs font-semibold">{p.age_category}</span>}
      <h3 className="mt-3 font-display text-lg font-semibold text-[#0B1F44]">{p.name}</h3>
      {p.playing_category && <p className="text-sm text-blue-700 font-medium">{p.playing_category}</p>}
      {p.club && <p className="text-sm text-slate-500">{p.club}</p>}
    </div>
  </article>
);

const pickById = (items, ids) => (items && ids?.length ? ids.map((id) => items.find((x) => x.id === id)).filter(Boolean) : []);

export const Welcome = ({ s }) => {
  if (!s.welcome_title && !s.welcome_message) return null;
  return (
    <section className="py-16 md:py-20 bg-white border-b border-slate-100" data-testid="home-welcome-section">
      <Container className="grid gap-6 lg:grid-cols-3 items-start">
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B1F44]" data-testid="welcome-title">{s.welcome_title}</h2>
        <p className="lg:col-span-2 text-base md:text-lg text-slate-600 leading-relaxed whitespace-pre-line" data-testid="welcome-message">{s.welcome_message}</p>
      </Container>
    </section>
  );
};

export const FeaturedBearers = ({ ids }) => {
  const { items } = useContent("bearers");
  const list = pickById(items, ids);
  if (!list.length) return null;
  return (
    <section className="py-20 md:py-28 bg-white" data-testid="home-featured-bearers">
      <Container>
        <SectionHeader eyebrow="Leadership" title="Meet our office bearers" action={<MoreLink to="/office-bearers" testid="home-bearers-all-link">All office bearers</MoreLink>} />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{list.map((p) => <Person key={p.id} p={p} />)}</div>
      </Container>
    </section>
  );
};

export const FeaturedPlayers = ({ ids }) => {
  const { items } = useContent("players");
  const list = pickById(items, ids);
  if (!list.length) return null;
  return (
    <section className="py-20 md:py-28 bg-slate-50" data-testid="home-featured-players">
      <Container>
        <SectionHeader eyebrow="Players" title="Featured players" action={<MoreLink to="/players" testid="home-players-all-link">Player directory</MoreLink>} />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{list.map((p) => <PlayerCard key={p.id} p={p} />)}</div>
      </Container>
    </section>
  );
};

export const Sponsors = () => {
  const { items } = useContent("sponsors");
  if (!items?.length) return null;
  return (
    <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200" data-testid="home-sponsors-section">
      <Container>
        <p className="eyebrow text-center mb-3">Supported by</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-center text-[#0B1F44] mb-10">Our sponsors & partners</h2>
        <div className="flex flex-wrap justify-center gap-5">
          {items.map((sp) => {
            const inner = (
              <>
                <div className="h-20 w-full grid place-items-center">
                  {sp.logo ? <img src={mediaUrl(sp.logo)} alt={sp.name} className="max-h-20 max-w-full object-contain" /> : <span className="font-display text-lg font-bold text-[#0B1F44]">{sp.name}</span>}
                </div>
                <p className="mt-3 text-sm font-semibold text-[#0B1F44] text-center">{sp.name}</p>
                {sp.tier && <p className="text-xs text-blue-700 text-center">{sp.tier}</p>}
                {sp.description && <p className="mt-2 text-xs text-slate-500 text-center line-clamp-2">{sp.description}</p>}
                {sp.website && <ExternalLink className="absolute top-3 right-3 h-3.5 w-3.5 text-slate-300" />}
              </>
            );
            const cls = "card relative w-52 p-5 flex flex-col items-center";
            return sp.website
              ? <a key={sp.id} href={sp.website} target="_blank" rel="noreferrer" className={cls} data-testid={`sponsor-${sp.id}`}>{inner}</a>
              : <article key={sp.id} className={cls} data-testid={`sponsor-${sp.id}`}>{inner}</article>;
          })}
        </div>
      </Container>
    </section>
  );
};
